import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { whatsappApi, formatApiError } from '../services/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import {
  WhatsappLogo, CheckCircle, XCircle, ArrowLeft, PaperPlaneTilt,
  ShieldCheck, LinkBreak, CopySimple, Eye, EyeSlash,
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL;

const Field = ({ label, hint, children, extra }) => (
  <div className="space-y-1.5">
    <Label className="overline">{label}</Label>
    <div className="flex gap-2">{children}</div>
    {hint && <p className="text-xs text-[#8A94A6]">{hint}</p>}
    {extra}
  </div>
);

const IntegrationWhatsApp = () => {
  const navigate = useNavigate();
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [testPhone, setTestPhone] = useState('');
  const [testBody, setTestBody] = useState('Test-Nachricht aus dem Management-System.');
  const [testing, setTesting] = useState(false);
  const [showToken, setShowToken] = useState(false);
  const [showSecret, setShowSecret] = useState(false);

  const [form, setForm] = useState({
    access_token: '',
    phone_number_id: '',
    waba_id: '',
    verify_token: '',
    app_secret: '',
    api_version: 'v20.0',
  });

  const webhookUrl = `${API}/api/whatsapp/webhook`;
  const connected = cfg?.connection_status === 'connected';

  const load = async () => {
    setLoading(true);
    try {
      const res = await whatsappApi.getConfig();
      setCfg(res.data);
      setForm({
        access_token: '',
        phone_number_id: res.data.phone_number_id || '',
        waba_id: res.data.waba_id || '',
        verify_token: '',
        app_secret: '',
        api_version: res.data.api_version || 'v20.0',
      });
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async () => {
    setSaving(true);
    try {
      const body = {};
      Object.entries(form).forEach(([k, v]) => {
        if (v !== '' && v !== undefined) body[k] = v;
      });

      if (Object.keys(body).length === 0) {
        toast.info('Keine Änderungen zum Speichern');
        setSaving(false);
        return;
      }

      await whatsappApi.updateConfig(body);
      toast.success('Konfiguration gespeichert');
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const verify = async () => {
    setVerifying(true);
    try {
      const res = await whatsappApi.verify();
      if (res.data.ok) {
        const m = res.data.meta || {};
        toast.success(`Verbunden mit ${m.verified_name || m.display_phone_number}`, {
          description: `Qualität: ${m.quality_rating || '—'}`,
        });
      } else {
        toast.error('Verbindung fehlgeschlagen', {
          description: res.data.error,
        });
      }
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setVerifying(false);
    }
  };

  const disconnect = async () => {
    if (!window.confirm(
      'Verbindung wirklich trennen? Die WhatsApp-Konfiguration wird aus der Datenbank entfernt.'
    )) return;

    setSaving(true);
    try {
      await whatsappApi.disconnect();
      toast.success('Verbindung getrennt');
      await load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  const sendTest = async () => {
    if (!testPhone) {
      toast.error('Bitte Telefonnummer eintragen');
      return;
    }

    setTesting(true);
    try {
      const res = await whatsappApi.test({
        phone: testPhone,
        body: testBody
      });
      toast.success(`Testnachricht an ${res.data.to} gesendet`);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setTesting(false);
    }
  };

  const copyWebhook = async () => {
    try {
      await navigator.clipboard.writeText(webhookUrl);
      toast.success('Webhook-URL kopiert');
    } catch {
      toast.error('Kopieren nicht möglich');
    }
  };

  if (loading) {
    return (
      <div className="p-8 text-center text-[#525866]">
        Lade Konfiguration …
      </div>
    );
  }

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-6" data-testid="wa-integration-page">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={() => navigate('/settings')}>
          <ArrowLeft weight="bold" size={16} className="mr-1" /> Einstellungen
        </Button>
        <span className="text-[#8A94A6]">/</span>
        <span className="text-sm text-[#525866]">Integrationen</span>
        <span className="text-[#8A94A6]">/</span>
        <span className="text-sm font-semibold text-[#111418]">WhatsApp Business</span>
      </div>

      <div className="card-swiss p-6">
        <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-4">
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-lg bg-[#25D366] flex items-center justify-center">
              <WhatsappLogo weight="fill" size={26} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight text-[#111418]">
                WhatsApp Business
              </h1>
              <p className="text-sm text-[#525866]">
                Anbieter: <span className="font-semibold">Meta Cloud API</span>
              </p>
            </div>
          </div>

          <div className="flex items-center gap-2" data-testid="wa-connection-status">
            {connected ? (
              <span className="inline-flex items-center gap-1 text-sm font-semibold px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 border border-emerald-200">
                <CheckCircle weight="fill" size={16} /> Verbunden
              </span>
            ) : (
              <span className="inline-flex items-center gap-1 text-sm font-semibold px-3 py-1 rounded-full bg-red-50 text-red-700 border border-red-200">
                <XCircle weight="fill" size={16} /> Nicht verbunden
              </span>
            )}
          </div>
        </div>
      </div>

      <div className="card-swiss p-6 space-y-5">
        <h2 className="text-lg font-semibold text-[#111418]">Zugangsdaten</h2>

        <Field
          label="WhatsApp Business Account ID (WABA)"
          hint="Optional — nur nötig wenn du Templates verwaltest"
        >
          <Input
            value={form.waba_id}
            onChange={(e) => setForm({ ...form, waba_id: e.target.value })}
            className="input-swiss"
            placeholder="1234567890"
            data-testid="wa-waba-input"
          />
        </Field>

        <Field label="Phone Number ID *">
          <Input
            value={form.phone_number_id}
            onChange={(e) => setForm({ ...form, phone_number_id: e.target.value })}
            className="input-swiss"
            placeholder="9876543210"
            data-testid="wa-phone-id-input"
          />
        </Field>

        <Field
          label="Access Token *"
          hint={cfg?.access_token_set
            ? `Aktuell gespeichert: ${cfg.access_token_masked}. Feld leer lassen um Token beizubehalten.`
            : 'System-User-Token aus dem Meta Business Manager'}
        >
          <Input
            type={showToken ? 'text' : 'password'}
            value={form.access_token}
            onChange={(e) => setForm({ ...form, access_token: e.target.value })}
            className="input-swiss"
            placeholder={cfg?.access_token_set ? '••• leer = beibehalten' : 'EAAxxxxxxxxxxxxxxxxx'}
            data-testid="wa-access-token-input"
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => setShowToken(!showToken)}
          >
            {showToken
              ? <EyeSlash weight="bold" size={16} />
              : <Eye weight="bold" size={16} />
            }
          </Button>
        </Field>

        <Field
          label="Webhook Verify Token *"
          hint={cfg?.verify_token_set
            ? `Aktuell gespeichert: ${cfg.verify_token_masked}. Muss bei Meta im Webhook-Setup übereinstimmen.`
            : 'Frei wählbarer geheimer String — bei Meta identisch eintragen'}
        >
          <Input
            value={form.verify_token}
            onChange={(e) => setForm({ ...form, verify_token: e.target.value })}
            className="input-swiss"
            placeholder={cfg?.verify_token_set ? '••• leer = beibehalten' : 'mein_geheimer_verify_token'}
            data-testid="wa-verify-token-input"
          />
        </Field>

        <Field
          label="App Secret (optional)"
          hint={cfg?.app_secret_set
            ? `Aktuell: ${cfg.app_secret_masked} — aktiviert HMAC-Signaturprüfung eingehender Webhooks`
            : 'Für sicheren Betrieb empfohlen — aktiviert Signatur-Prüfung eingehender Nachrichten'}
        >
          <Input
            type={showSecret ? 'text' : 'password'}
            value={form.app_secret}
            onChange={(e) => setForm({ ...form, app_secret: e.target.value })}
            className="input-swiss"
            placeholder={cfg?.app_secret_set ? '••• leer = beibehalten' : 'App Secret aus Meta App Dashboard'}
            data-testid="wa-app-secret-input"
          />
          <Button
            type="button"
            variant="outline"
            onClick={() => setShowSecret(!showSecret)}
          >
            {showSecret
              ? <EyeSlash weight="bold" size={16} />
              : <Eye weight="bold" size={16} />
            }
          </Button>
        </Field>

        <Field label="API Version">
          <Input
            value={form.api_version}
            onChange={(e) => setForm({ ...form, api_version: e.target.value })}
            className="input-swiss max-w-[120px]"
            placeholder="v20.0"
            data-testid="wa-api-version-input"
          />
        </Field>

        <Field label="Webhook-URL (bei Meta hinterlegen)">
          <div className="flex items-center gap-2 flex-1 px-3 py-2 bg-[#F5F7FA] border border-[#E1E4E8] rounded font-mono text-xs">
            {webhookUrl}
          </div>
          <Button
            type="button"
            variant="outline"
            onClick={copyWebhook}
            data-testid="wa-copy-webhook-btn"
          >
            <CopySimple weight="bold" size={16} className="mr-1" /> Kopieren
          </Button>
        </Field>

        <div className="pt-3 flex flex-wrap gap-2 border-t border-[#E2E4E9]">
          <Button
            onClick={save}
            disabled={saving}
            className="btn-primary"
            data-testid="wa-save-btn"
          >
            {saving ? 'Speichere …' : 'Speichern'}
          </Button>

          <Button
            type="button"
            variant="outline"
            onClick={verify}
            disabled={verifying || !connected}
            data-testid="wa-verify-btn"
          >
            <ShieldCheck weight="bold" size={16} className="mr-1" />
            {verifying ? 'Prüfe …' : 'Verbindung prüfen'}
          </Button>

          <Button
            type="button"
            variant="outline"
            className="border-red-300 text-red-700 hover:bg-red-50"
            onClick={disconnect}
            disabled={saving || !cfg?.access_token_set}
            data-testid="wa-disconnect-btn"
          >
            <LinkBreak weight="bold" size={16} className="mr-1" />
            Verbindung trennen
          </Button>
        </div>
      </div>

      <div className="card-swiss p-6 space-y-4">
        <h2 className="text-lg font-semibold text-[#111418]">
          Testnachricht senden
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="overline">Telefonnummer (E.164)</Label>
            <Input
              value={testPhone}
              onChange={(e) => setTestPhone(e.target.value)}
              placeholder="+491701234567"
              className="input-swiss"
              data-testid="wa-test-phone-input"
            />
          </div>

          <div>
            <Label className="overline">Nachricht</Label>
            <Input
              value={testBody}
              onChange={(e) => setTestBody(e.target.value)}
              className="input-swiss"
              data-testid="wa-test-body-input"
            />
          </div>
        </div>

        <Button
          onClick={sendTest}
          disabled={testing || !connected}
          className="bg-[#25D366] hover:bg-[#128C7E] text-white"
          data-testid="wa-test-send-btn"
        >
          <PaperPlaneTilt weight="fill" size={16} className="mr-2" />
          {testing ? 'Sende …' : 'Testnachricht senden'}
        </Button>

        {!connected && (
          <p className="text-xs text-[#8A94A6]">
            Test-Versand ist erst nach dem Speichern gültiger Zugangsdaten aktiv.
          </p>
        )}
      </div>
    </div>
  );
};

export default IntegrationWhatsApp;
